import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";

function notificationTitle(row) {
  const data = row?.data || {};
  if (data.title) return data.title;
  if (data.type === "announcement") return data.title || "Announcement";
  return "Notification";
}

function notificationMessage(row) {
  const data = row?.data || {};
  return data.message || "";
}

function notificationTypeLabel(row) {
  const type = row?.type || "";
  if (type.includes("MobileRevisionSupportAssigned")) return "Revision support";
  if (type.includes("Announcement")) return "Announcement";
  return "Update";
}

const TutorNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [marking, setMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("tutor/notifications", { params: { per_page: 50 } });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setUnreadCount(res.data?.meta?.unread_count ?? 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await api.post(`tutor/notifications/${id}/mark-as-read`);
      await fetchNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to mark notification as read.");
    }
  };

  const markAllRead = async () => {
    setMarking(true);
    try {
      await api.post("tutor/notifications/mark-all-as-read");
      toast.success("All notifications marked as read.");
      await fetchNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to mark all as read.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assignments, announcements, and other updates for your tutor account.
          </Typography>
        </Box>
        {unreadCount > 0 ? (
          <Button variant="outlined" size="small" onClick={markAllRead} disabled={marking}>
            {marking ? "Marking…" : "Mark all read"}
          </Button>
        ) : null}
      </Box>

      <Paper sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, px: 2, textAlign: "center" }}>
            No notifications yet.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((row, index) => {
              const unread = !row.read_at;
              return (
                <React.Fragment key={row.id}>
                  {index > 0 ? <Divider /> : null}
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      unread ? (
                        <Button size="small" onClick={() => markRead(row.id)}>
                          Mark read
                        </Button>
                      ) : (
                        <Chip size="small" label="Read" variant="outlined" />
                      )
                    }
                    sx={{
                      py: 2,
                      px: 2,
                      bgcolor: unread ? "rgba(59, 42, 159, 0.04)" : "transparent",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 10 }}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {notificationTitle(row)}
                          </Typography>
                          <Chip size="small" label={notificationTypeLabel(row)} variant="outlined" />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                            {notificationMessage(row)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            {row.created_at_human || row.created_at}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default TutorNotifications;
